import { Check, Search } from 'lucide-react'
import { useState } from 'react'
import { lifespan } from '../../shared/tree'
import { Avatar } from '@/components/tree/Avatar'
import { Input } from '@/components/ui/input'
import { findIn, MAX_BRING, useFramily, type FramilyPerson } from '@/lib/framily'
import { cn } from '@/lib/utils'

// "Add from your connections" when making a circle: people from your other circles to start its tree with. They're copied
// in with the links between them (and you with them); your other circles don't change. The ones with an account can
// be invited to join it too.
export function FramilyPicker({
  value,
  onChange,
  invite,
  onInvite,
}: {
  value: string[]
  onChange: (ids: string[]) => void
  invite: boolean
  onInvite: (invite: boolean) => void
}) {
  const framily = useFramily()
  const [search, setSearch] = useState('')
  const others = (framily.data ?? []).filter((p) => !p.me)
  if (others.length === 0) return null
  const picked = new Set(value)
  const shown = findIn(others, search)
  const full = value.length >= MAX_BRING
  const toggle = (id: string) => onChange(picked.has(id) ? value.filter((x) => x !== id) : [...value, id])
  const accounts = others.filter((p) => picked.has(p.id) && p.account).length

  return (
    <fieldset className="mt-3 grid gap-3">
      <legend className="mb-1 text-sm font-medium">Add from your connections</legend>
      <p className="text-sub text-muted-foreground">
        People from your other circles, with how they’re linked. They’re copied into this circle, so your other circles
        stay as they are.
      </p>
      {others.length > 8 && (
        <div className="relative">
          <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            type="search"
            aria-label="Search your connections"
            placeholder="Search by name or circle"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}
      <ul className="grid max-h-96 gap-1 overflow-y-auto rounded-2xl border border-border p-1">
        {shown.map((p) => (
          <li key={p.id}>
            <Row person={p} checked={picked.has(p.id)} disabled={full && !picked.has(p.id)} onChange={toggle} />
          </li>
        ))}
        {shown.length === 0 && <li className="px-3 py-4 text-sub text-muted-foreground">No one by that name.</li>}
      </ul>
      {value.length > 0 && (
        <p className="text-sub text-muted-foreground">
          {value.length} picked{full ? ', as many as a new circle can start with' : ''}. You’re added too.
        </p>
      )}
      {accounts > 0 && (
        <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={invite}
            onChange={(e) => onInvite(e.target.checked)}
          />
          Invite the {accounts === 1 ? 'one' : accounts} with an account to join
        </label>
      )}
    </fieldset>
  )
}

function Row({
  person,
  checked,
  disabled,
  onChange,
}: {
  person: FramilyPerson
  checked: boolean
  disabled: boolean
  onChange: (id: string) => void
}) {
  const years = person.living ? null : lifespan(person)
  const about = [person.pet ? 'Pet' : null, years, person.circles.join(', ')].filter(Boolean).join(' · ')
  return (
    <label
      className={cn(
        'flex min-h-14 cursor-pointer items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-secondary has-checked:bg-secondary has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(person.id)}
      />
      <Avatar person={person} className="size-9 text-sm" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">{person.name}</span>
        <span className="truncate text-sub text-muted-foreground">{about}</span>
      </span>
      <span
        aria-hidden="true"
        className={cn(
          'grid size-6 shrink-0 place-items-center rounded-md border-2',
          checked ? 'border-brand bg-brand text-brand-foreground' : 'border-input',
        )}
      >
        {checked && <Check className="size-3.5" strokeWidth={3} />}
      </span>
    </label>
  )
}
